import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Heading,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Text,
  VStack,
  useToast,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  useDisclosure,
  Link,
  OrderedList,
  ListItem,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiErrorMessage, CrossChainFees } from '../api/client';
import { PAYOUT_OPTIONS, payoutOptionKey, findPayoutOption } from '../utils/crossChain';

const MAX_FEE_PERCENTAGE = 10;

// Settings for the partner's USDC/USDT fee on Breez SDK - Spark,
// charged through their Flashnet Orchestra affiliate.
const CrossChainFeeSettings: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: settings, error, isLoading, isFetching } = useQuery({
    queryKey: ['cross-chain-fees'],
    queryFn: async () => (await api.crossChain.getFees()).data,
    retry: false,
  });
  const unavailable = (error as any)?.response?.status === 503;

  const [feePercentageInput, setFeePercentageInput] = React.useState<string>('0');
  const [payoutKey, setPayoutKey] = React.useState<string>(
    payoutOptionKey(PAYOUT_OPTIONS[0].chain, PAYOUT_OPTIONS[0].asset)
  );
  const [payoutAddress, setPayoutAddress] = React.useState<string>('');

  // Shows the given settings in the form
  const showSettings = React.useCallback((loaded: CrossChainFees) => {
    setFeePercentageInput(loaded.feePercentage.toString());
    if (loaded.payoutChain && loaded.payoutAsset) {
      setPayoutKey(payoutOptionKey(loaded.payoutChain, loaded.payoutAsset));
    }
    setPayoutAddress(loaded.payoutAddress || '');
  }, []);

  // Update local state when the settings are loaded
  React.useEffect(() => {
    if (settings) showSettings(settings);
  }, [settings, showSettings]);

  // Everything on the Fees page can change when the fee is turned on or off
  const reloadCrossChain = () =>
    Promise.all(
      ['cross-chain-fees', 'cross-chain-stats', 'cross-chain-orders', 'cross-chain-claims'].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      )
    );

  const updateMutation = useMutation({
    mutationFn: api.crossChain.updateFees,
    onSuccess: () => {
      reloadCrossChain();
      toast({
        title: 'Success',
        description: 'USDC/USDT fee settings updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      // The settings shown may no longer be current: changed elsewhere, or a
      // change whose outcome is unknown (including when no response arrived).
      // Reload them and show them, even if unchanged (the query then keeps
      // the same data, so the effect above wouldn't reset what the partner
      // typed). Other errors keep the input.
      const code = error?.response?.data?.code;
      if (!error?.response || code === 'settings_changed' || code === 'not_confirmed') {
        reloadCrossChain().then(() => {
          const latest = queryClient.getQueryData<CrossChainFees>(['cross-chain-fees']);
          if (latest) showSettings(latest);
        });
      }
      toast({
        title: 'Error',
        description: error?.response
          ? apiErrorMessage(error, 'Failed to update USDC/USDT fee settings')
          : 'There was an issue updating. Please check the settings and try again.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    },
  });

  const showError = (description: string) =>
    toast({ title: 'Error', description, status: 'error', duration: 5000, isClosable: true });

  const handleSave = () => {
    const feePercentage = parseFloat(feePercentageInput);
    if (isNaN(feePercentage) || feePercentage < 0 || feePercentage > MAX_FEE_PERCENTAGE) {
      showError(`Fee percentage must be between 0 and ${MAX_FEE_PERCENTAGE}`);
      return;
    }

    const option = findPayoutOption(payoutKey);
    if (feePercentage > 0) {
      if (!option) {
        showError('Choose a payout destination');
        return;
      }
      if (!payoutAddress.trim()) {
        showError('A payout address is required when the fee is greater than 0');
        return;
      }
    }

    updateMutation.mutate({
      feePercentage,
      payoutChain: option?.chain || '',
      payoutAsset: option?.asset || '',
      payoutAddress: payoutAddress.trim(),
      version: settings?.version ?? '',
    });
  };

  const feePercentage = parseFloat(feePercentageInput);
  const selectedOption = findPayoutOption(payoutKey);
  const turningOff = !!settings?.enabled && feePercentage === 0;

  return (
    <Box p={6} bg={cardBg} rounded="lg" border="1px" borderColor={borderColor}>
      <Heading size="md" mb={6}>
        USDC/USDT Fee Settings
      </Heading>

      {unavailable ? (
        <Text color="gray.600">USDC/USDT fees are not available yet.</Text>
      ) : error ? (
        <Text color="gray.600">{apiErrorMessage(error, 'Failed to load USDC/USDT fee settings')}</Text>
      ) : (
        <VStack spacing={4} align="stretch">
          <Text color="gray.600">
            Set a fee (max allowed is {MAX_FEE_PERCENTAGE}%) on USDC/USDT payments made through your integration. By
            setting the fee below, you agree to the following{' '}
            <Link color="blue.500" textDecoration="underline" onClick={onOpen} cursor="pointer">
              terms
            </Link>
            .
          </Text>

          <FormControl>
            <FormLabel>Fee Percentage</FormLabel>
            <NumberInput
              min={0}
              max={MAX_FEE_PERCENTAGE}
              step={0.01}
              precision={2}
              value={feePercentageInput}
              onChange={(valueAsString, valueAsNumber) => {
                // Enforce the max value on input
                if (!isNaN(valueAsNumber) && valueAsNumber > MAX_FEE_PERCENTAGE) {
                  setFeePercentageInput(MAX_FEE_PERCENTAGE.toString());
                } else {
                  setFeePercentageInput(valueAsString);
                }
              }}
              isDisabled={isLoading || updateMutation.isPending}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          {feePercentage > 0 && (
            <>
              <FormControl isRequired>
                <FormLabel>Payout Destination</FormLabel>
                <Select
                  value={payoutKey}
                  onChange={(e) => setPayoutKey(e.target.value)}
                  isDisabled={updateMutation.isPending}
                >
                  {PAYOUT_OPTIONS.map((option) => (
                    <option key={payoutOptionKey(option.chain, option.asset)} value={payoutOptionKey(option.chain, option.asset)}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Payout Address</FormLabel>
                <Input
                  type="text"
                  fontFamily="mono"
                  placeholder={selectedOption?.placeholder}
                  value={payoutAddress}
                  onChange={(e) => setPayoutAddress(e.target.value)}
                  isDisabled={updateMutation.isPending}
                />
                <FormHelperText>
                  Claims are paid to this address. A new address only applies to fees earned after the change.
                </FormHelperText>
              </FormControl>
            </>
          )}

          {turningOff && (
            <Alert status="warning" rounded="md" fontSize="sm">
              <AlertIcon />
              Claim any available balance on the Fees page before turning the fee off. While the fee is off, balances
              can't be claimed, including amounts under the $1 claim minimum.
            </Alert>
          )}

          <Button
            alignSelf="flex-end"
            onClick={handleSave}
            isLoading={updateMutation.isPending}
            // Until a reload finishes the settings' version is stale, and a
            // save would be refused as "changed elsewhere"
            isDisabled={isLoading || isFetching}
          >
            Save
          </Button>
        </VStack>
      )}

      {/* Terms Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside" autoFocus={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">USDC/USDT Fee Terms</ModalHeader>
          <ModalBody>
            <Text mb={4}>
              By configuring a USDC/USDT fee within your Breez SDK integration, you agree to the following terms:
            </Text>
            <OrderedList spacing={4}>
              <ListItem>
                <Text as="strong">Fee Structure:</Text>
                <br />
                You may set a fee of up to {MAX_FEE_PERCENTAGE}% on top of the base fees for USDC/USDT payments
                processed through your integration with Breez SDK - Spark 0.27 or later, including sends, receives and
                payment links.
              </ListItem>

              <ListItem>
                <Text as="strong">Flashnet Share:</Text>
                <br />
                USDC/USDT payments are processed by Flashnet Orchestra, which keeps 20% of your fee. You earn the
                remaining 80%. See{' '}
                <Link
                  href="https://docs.flashnet.xyz/orchestra/fees"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="blue.500"
                  textDecoration="underline"
                >
                  Flashnet's fee documentation
                </Link>
                {' '}for how fees are calculated.
              </ListItem>

              <ListItem>
                <Text as="strong">Payouts:</Text>
                <br />
                Fees build up in the asset each payment settles in (USDC on Solana or USDB on Spark) and are paid out
                when you claim them from the Fees page. A claim pays out the full available balance, needs at least $1,
                and goes to the payout address that was set when the fees were earned. The amount delivered can be
                slightly lower because of conversion and payout costs.
              </ListItem>

              <ListItem>
                <Text as="strong">Fee Adjustments:</Text>
                <br />
                You may change your fee or payout address at any time. Changes take effect immediately for new
                payments. You will be emailed whenever these settings change.
              </ListItem>

              <ListItem>
                <Text as="strong">Compliance:</Text>
                <br />
                You are solely responsible for ensuring that your fee structure complies with all applicable laws and
                regulations in your jurisdiction.
              </ListItem>

              <ListItem>
                <Text as="strong">Termination:</Text>
                <br />
                Breez and/or Flashnet may terminate this agreement at any time.
              </ListItem>
            </OrderedList>
          </ModalBody>

          <ModalFooter justifyContent="center">
            <Button onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CrossChainFeeSettings;
